import React, { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { signOutSignupIntake } from '../../utils/signOutSignupFlow'

/**
 * SignupLayout — shared shell for the /signup funnel.
 *
 * Two themes:
 *   - 'light' (default) — full-width centered, off-white bg, no
 *     right-column photography. Used by every screen in the
 *     redesigned funnel.
 *   - 'dark' — original split-screen layout with photo on the right.
 *     No active route uses it today; kept for any future intake
 *     screen that wants the photo + dark canvas treatment.
 *
 * Light layout:
 *
 *   ┌─────────────────────────────────────┐
 *   │  [logo] [back?]      [aux link?]    │  ← 80px header
 *   ├─────────────────────────────────────┤
 *   │                                     │
 *   │      [progress (optional)]          │  ← max-w-[520px], centered
 *   │                                     │
 *   │      <children>                     │
 *   │                                     │
 *   └─────────────────────────────────────┘
 *
 * Props:
 *   theme         — 'light' (default) | 'dark'
 *   imageSrc/Alt  — only honoured in dark theme
 *   showBack      — render a bare ← back glyph next to the wordmark
 *   signOutOnBack — when true, back signs out and returns to /signup/account
 *   backTo        — explicit back destination (e.g. "/" for landing page)
 *   showLogin     — render "Already have an account? Log in" top-right
 *   step/totalSteps — render a 2px progress bar above children
 *   children      — the page content
 */

// Direct-link fallback if there's no history to pop.
const FALLBACK_BACK = {
  '/signup/account': '/signup',
  '/signup/community': '/signup/account',
  '/signup/academy/book': '/signup/account',
}

export default function SignupLayout({
  theme = 'light',
  imageSrc,
  imageAlt = '',
  showBack = false,
  signOutOnBack = false,
  backTo = null,
  showLogin = true,
  step,
  totalSteps,
  // Mobile-only: vertically center the body content and pin an
  // optional slot to the bottom of the viewport. Opt-in so other
  // signup screens (top-aligned forms) stay unchanged.
  centerOnMobile = false,
  mobileBottomSlot = null,
  children,
}) {
  const navigate = useNavigate()
  const location = useLocation()
  const [backBusy, setBackBusy] = useState(false)

  const handleBack = async () => {
    if (backBusy) return
    if (signOutOnBack) {
      setBackBusy(true)
      try {
        await signOutSignupIntake()
      } catch (err) {
        console.warn('SignupLayout: sign-out on back failed', err)
      } finally {
        navigate('/signup/account', { replace: true })
        setBackBusy(false)
      }
      return
    }
    if (backTo) {
      navigate(backTo, { replace: true })
      return
    }
    if (window.history.length > 1) {
      navigate(-1)
      return
    }
    navigate(FALLBACK_BACK[location.pathname] || '/')
  }

  const showProgress =
    typeof step === 'number' &&
    typeof totalSteps === 'number' &&
    step >= 1 &&
    step <= totalSteps
  const pct = showProgress ? Math.round((step / totalSteps) * 100) : 0

  if (theme === 'dark') {
    return (
      <DarkLayout
        imageSrc={imageSrc}
        imageAlt={imageAlt}
        showBack={showBack}
        signOutOnBack={signOutOnBack}
        backBusy={backBusy}
        showLogin={showLogin}
        showProgress={showProgress}
        step={step}
        totalSteps={totalSteps}
        pct={pct}
        onBack={handleBack}
      >
        {children}
      </DarkLayout>
    )
  }

  return (
    <LightLayout
      showBack={showBack}
      backBusy={backBusy}
      showLogin={showLogin}
      showProgress={showProgress}
      step={step}
      totalSteps={totalSteps}
      pct={pct}
      onBack={handleBack}
      centerOnMobile={centerOnMobile}
      mobileBottomSlot={mobileBottomSlot}
    >
      {children}
    </LightLayout>
  )
}

/* ─────────────────────────  LIGHT THEME  ───────────────────────── */

function LightLayout({
  showBack,
  backBusy = false,
  showLogin,
  showProgress,
  step,
  totalSteps,
  pct,
  onBack,
  centerOnMobile,
  mobileBottomSlot,
  children,
}) {
  // svh = small viewport height, locked to the smallest visible
  // viewport state on mobile (URL bar showing). 100dvh would jump
  // as the URL bar shows/hides; svh keeps content stable.
  return (
    <div
      className="min-h-screen min-h-[100svh] font-sans antialiased flex flex-col bg-[var(--signup-bg-base)] text-[var(--signup-text-primary)]"
      style={{
        fontFeatureSettings: '"cv11", "ss03", "cv05"',
        textRendering: 'optimizeLegibility',
      }}
    >
      {/* Header — 80px, no separator, blends into page */}
      <header className="h-20 flex items-center justify-between px-6 sm:px-10 shrink-0">
        <div className="flex items-center gap-2">
          {showBack && (
            <button
              type="button"
              onClick={onBack}
              disabled={backBusy}
              aria-label="Back"
              className="-ml-2.5 inline-flex h-11 w-11 items-center justify-center rounded-sm text-[var(--signup-text-secondary)] hover:text-[var(--signup-text-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--signup-border-strong)] motion-safe:transition-colors motion-safe:duration-150 disabled:opacity-50"
            >
              <ArrowLeft size={20} strokeWidth={1.75} aria-hidden />
            </button>
          )}
          <Link
            to="/"
            aria-label="Statbox home"
            className="flex items-center gap-2"
          >
            <img
              src="/Logo_Statbox_Transparent.png"
              alt=""
              className="h-7 w-auto"
            />
            {/* Wordmark is a white PNG; CSS-invert on light bg gives a
                subtle near-black wordmark without shipping a new asset. */}
            <img
              src="/logo_white.png"
              alt="Statbox"
              className="h-4 w-auto opacity-90"
              style={{ filter: 'invert(1)' }}
            />
          </Link>
        </div>

        {showLogin && (
          /* Header-right log-in is desktop-only — on mobile the
             three-element row collides with the wordmark, so the
             funnel screens that need a second log-in entry point
             pin it to the bottom via `mobileBottomSlot` instead. */
          <Link
            to="/login"
            className="hidden md:inline-block text-[13px] text-[var(--signup-text-secondary)] hover:text-[var(--signup-text-primary)] motion-safe:transition-colors motion-safe:duration-150"
          >
            Already have an account?{' '}
            <span className="text-[var(--signup-accent)] font-medium">
              Log in
            </span>
          </Link>
        )}
      </header>

      {/* Body — top-aligned by default (existing pages). When
          centerOnMobile is set, mobile flips to justify-center so
          short pages fill the viewport with vertically-balanced
          content; desktop keeps the top-aligned form layout. */}
      <div
        className={`flex-1 flex flex-col items-center px-6 sm:px-10 pt-10 sm:pt-14 pb-10 sm:pb-16 ${
          centerOnMobile ? 'justify-center md:justify-start' : ''
        }`}
      >
        <div className="w-full max-w-[520px]">
          {showProgress && (
            <div className="mb-12">
              <div
                className="h-[2px] w-full rounded-full bg-[var(--signup-bg-muted)] overflow-hidden"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={totalSteps}
                aria-valuenow={step}
                aria-label={`Step ${step} of ${totalSteps}`}
              >
                <div
                  className="h-full bg-[var(--signup-accent)] motion-safe:transition-[width]"
                  style={{
                    width: `${pct}%`,
                    transitionDuration: '600ms',
                    transitionTimingFunction: 'var(--ease-out-expo)',
                  }}
                />
              </div>
              <p className="mt-3 text-[13px] text-[var(--signup-text-tertiary)] tabular-nums">
                Step {step} of {totalSteps}
              </p>
            </div>
          )}
          {children}
        </div>
      </div>

      {/* Mobile-only bottom slot — pinned to the bottom of the
          viewport with safe-area padding so iOS home-indicator
          devices don't clip the link. Desktop renders the same
          content inline inside `children` instead. */}
      {mobileBottomSlot && (
        <div
          className="md:hidden shrink-0 px-6 pt-4"
          style={{ paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom))' }}
        >
          {mobileBottomSlot}
        </div>
      )}
    </div>
  )
}

/* ─────────────────────────  DARK THEME  ─────────────────────────
   No active route renders this today (the venue-contact stub that
   last used it has been replaced by the venue wizard). Kept around
   as the prior shared layout, in case a future intake screen wants
   the photo + dark canvas treatment. */

function DarkLayout({
  imageSrc,
  imageAlt,
  showBack,
  backBusy = false,
  showLogin,
  showProgress,
  step,
  totalSteps,
  pct,
  onBack,
  children,
}) {
  return (
    <div
      className="min-h-screen min-h-[100dvh] bg-black text-white font-sans antialiased flex flex-col lg:grid lg:grid-cols-[55fr_45fr]"
      style={{
        fontFeatureSettings: '"cv11", "ss03", "cv05"',
        textRendering: 'optimizeLegibility',
      }}
    >
      <div className="relative h-[240px] sm:h-[280px] lg:h-auto lg:order-2 overflow-hidden">
        {imageSrc && (
          <>
            <img
              src={imageSrc}
              alt={imageAlt}
              className="absolute inset-0 w-full h-full object-cover object-center"
              style={{ filter: 'saturate(0.85) contrast(1.02)' }}
            />
            <div
              aria-hidden
              className="absolute inset-0 mix-blend-overlay opacity-[0.18] pointer-events-none"
              style={{
                background:
                  'linear-gradient(135deg, rgba(113,205,217,0.50) 0%, rgba(58,83,164,0.25) 60%, transparent 100%)',
              }}
            />
            <div
              aria-hidden
              className="absolute inset-0 pointer-events-none lg:hidden"
              style={{
                background:
                  'linear-gradient(to bottom, transparent 55%, rgba(0,0,0,0.55) 100%)',
              }}
            />
            <div
              aria-hidden
              className="hidden lg:block absolute inset-0 pointer-events-none"
              style={{
                background:
                  'linear-gradient(to right, rgba(0,0,0,0.42) 0%, rgba(0,0,0,0.08) 14%, transparent 32%)',
              }}
            />
          </>
        )}
      </div>

      <div className="relative flex-1 lg:order-1 flex flex-col min-h-0">
        <header className="flex items-center justify-between px-6 sm:px-8 pt-5 sm:pt-6">
          <div className="flex items-center gap-3">
            {showBack && (
              <button
                type="button"
                onClick={onBack}
                disabled={backBusy}
                aria-label="Back"
                className="text-white/70 hover:text-white -ml-1.5 p-1.5 rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40 motion-safe:transition-colors motion-safe:duration-150 disabled:opacity-50"
              >
                <ArrowLeft size={20} strokeWidth={1.75} aria-hidden />
              </button>
            )}
            <Link
              to="/"
              aria-label="Statbox home"
              className="flex items-center gap-2"
            >
              <img
                src="/Logo_Statbox_Transparent.png"
                alt=""
                className="h-7 w-auto"
              />
              <img
                src="/logo_white.png"
                alt="Statbox"
                className="h-4 w-auto opacity-95"
              />
            </Link>
          </div>

          {showLogin && (
            <Link
              to="/login"
              className="text-[13px] text-white/55 hover:text-white motion-safe:transition-colors motion-safe:duration-150"
            >
              Already have an account?{' '}
              <span className="text-white/85">Log in</span>
            </Link>
          )}
        </header>

        <div className="flex-1 flex flex-col items-center justify-center px-6 sm:px-10 py-10 sm:py-14">
          <div className="w-full max-w-[440px]">
            {showProgress && (
              <div className="mb-12">
                <div
                  className="h-[2px] w-full rounded-full bg-white/[0.08] overflow-hidden"
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={totalSteps}
                  aria-valuenow={step}
                  aria-label={`Step ${step} of ${totalSteps}`}
                >
                  <div
                    className="h-full bg-[#71cdd9] motion-safe:transition-[width] motion-safe:duration-500 motion-safe:ease-out"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <p className="mt-3 text-[13px] text-white/55 tabular-nums">
                  Step {step} of {totalSteps}
                </p>
              </div>
            )}
            {children}
          </div>
        </div>
      </div>
    </div>
  )
}
